import type { QuotaMetric } from "@/lib/quotaGate"
import type { ReadingLevel } from "../contract"
import { PaywallDoor } from "../ds/gate"
import type { UpgradeSource } from "../pro/ProButton"

/**
 * How a reading is gated (handoff 3 §4, the paywall doc). The first notice and the first order are read in
 * full and end with one credit note. A paywall sits on the action that starts a reading, before it runs; if
 * the server still sends the computed preview (the glance and 01 in full, 02's heads), nothing on the page is
 * blurred, greyed or taken back: the sections that were not computed are listed with a door that opens the
 * paywall when pressed, and the rest of the page reads as it is.
 */

/** The doc's trigger number for a second notice (5) or a second order (8). */
const triggerOf = (metric: QuotaMetric): number => (metric === "nira.appeal_reading" ? 8 : 5)

export const isPreview = (level: ReadingLevel | undefined) => level === "preview"

/** The one mention of the free credit on a reading: at the foot of the free one, once. */
export function CreditNote({ of }: { of: "notice" | "order" }) {
  return (
    <p className="nrd-credit" role="note">
      {of === "notice"
        ? "This was your free notice analysis. Every notice after this is read in Nira Pro."
        : "This was your free Appeal or not reading. Every order after this is read in Nira Pro."}
    </p>
  )
}

/** A section the preview did not compute, listed in the rail; pressing it opens the paywall. */
export function LockedRailItem({ n, title, metric, source }: {
  n: string; title: string; line?: string; metric: QuotaMetric; source: UpgradeSource
}) {
  return (
    <PaywallDoor className="nrd-rail-lock" metric={metric} trigger={triggerOf(metric)} source={source}>
      <span className="mono">{n}</span><span className="nrd-rail-t">{title}</span>
    </PaywallDoor>
  )
}

/** After 02's heads: what the rest holds, said plainly, with the one door to it. Nothing is hidden behind it. */
export function PreviewDoor({ door, line, metric, source }: {
  door: string; title?: string; line: string; metric: QuotaMetric; source: UpgradeSource
}) {
  return (
    <div className="nr-preview" id="nrd-locked">
      <p className="nr-preview-line">{line}</p>
      <PaywallDoor className="nrd-door" metric={metric} trigger={triggerOf(metric)} source={source} badge>{door}</PaywallDoor>
    </div>
  )
}
